import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
  AbstractComponent,
  AuthenticationService,
  AuthorizationService,
  NavigationService,
  ObjectTools,
  SolActiveRouteStatus,
  SolBannerHostComponent,
  SolButtonGroupComponent,
  SolDetailsComponent,
  SolErrorPageComponent,
  SolErrorPageType,
  SolLoader,
  SolLoaderComponent,
  SolNavbarComponent,
  SolOverlaysComponent,
  SolPageBannerHostComponent,
  SolPageComponent,
  SolSelectValue,
  SolText,
  SolTopbarComponent,
  ThemeService,
} from '@sol/components';
import { Theme } from './models/theme';

class AppState {
  private constructor(
    public readonly showNavbar: boolean,
    public readonly hostClass: string,
    public readonly activeRouteStatus: SolActiveRouteStatus,
    public readonly theme: string | undefined,
    public readonly themes: SolSelectValue[],
  ) {}

  public static of(): AppState {
    return new AppState(true, '', 'loading', undefined, AppState.createThemes()).recalculate();
  }

  public withShowNavbar(showNavbar: boolean): AppState {
    if (this.showNavbar === showNavbar) {
      return this;
    }
    return this.copy({ showNavbar }).recalculate();
  }

  public withActiveRouteStatus(activeRouteStatus: SolActiveRouteStatus): AppState {
    if (this.activeRouteStatus === activeRouteStatus) {
      return this;
    }
    return this.copy({ activeRouteStatus });
  }

  public withTheme(theme: string | undefined): AppState {
    if (this.theme === theme) {
      return this;
    }
    return this.copy({ theme });
  }

  private recalculate(): AppState {
    return this.copy({ hostClass: this.showNavbar ? 'sol-app-shell' : '' });
  }

  private copy(props: Partial<AppState>): AppState {
    return ObjectTools.copy<AppState>(this, props);
  }

  private static createThemes(): SolSelectValue[] {
    return [
      {
        id: Theme.ASM_LIGHT,
        name: SolText.ofRaw('Legacy'),
      },
      {
        id: Theme.SOL_LIGHT,
        name: SolText.ofRaw('Light'),
      },
      {
        id: Theme.SOL_DARK,
        name: SolText.ofRaw('Dark'),
      },
    ];
  }
}

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    SolOverlaysComponent,
    SolBannerHostComponent,
    SolNavbarComponent,
    SolDetailsComponent,
    SolPageBannerHostComponent,
    SolPageComponent,
    SolTopbarComponent,
    SolButtonGroupComponent,
    SolLoaderComponent,
    SolErrorPageComponent,
  ],
  templateUrl: './app.component.html',
  host: { '[class]': 'hostClass$()' },
})
export class AppComponent extends AbstractComponent<AppState> implements OnInit {
  protected readonly SolErrorPageType = SolErrorPageType;
  protected readonly SolLoader = SolLoader;

  // Dependencies
  private readonly authenticationService = inject(AuthenticationService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly navigationService = inject(NavigationService);
  private readonly themeService = inject(ThemeService);

  // Computed
  protected readonly showNavbar$ = this.compute(state => state.showNavbar);
  protected readonly hostClass$ = this.compute(state => state.hostClass);
  protected readonly themes$ = this.compute(state => state.themes);
  protected readonly theme$ = this.compute(state => state.theme);
  protected readonly activeRouteStatus$ = this.compute(state => state.activeRouteStatus);

  constructor() {
    super(AppState.of());

    // Watch navigation
    this.navigationService.watchComputed(
      nss => nss.activeRouteStatus,
      status => this.update(state => state.withActiveRouteStatus(status)),
    );
    this.navigationService.watch(nss => {
      const route = nss.activeRoute;
      this.update(state => state.withShowNavbar(route?.showNavbar ?? true));
    });

    // Watch theme
    this.themeService.watchComputed(
      tss => tss.current,
      theme => this.update(state => state.withTheme(theme)),
    );
  }

  public ngOnInit(): void {
    this.authenticationService.fetch();
    this.authorizationService.setAuthorities([], true);
  }

  protected onThemeChange(theme: string | undefined): void {
    this.themeService.setTheme(theme as Theme);
  }
}
